/**
 * TechOrderTimeline · /tecnico/mis-ordenes
 * Vista de OTs del técnico en formato "Culebra" (Zig-Zag Timeline).
 * Estilo neumórfico CLARO de alto contraste.
 *
 * v4.0.0 — Fase 2 (tema claro)
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  Clock, MapPin, User, ChevronRight, RefreshCw,
  Zap, AlertTriangle, CheckCircle2, Loader2,
  LogOut, ArrowLeft, Wrench,
} from "lucide-react";

/* ═══════════════════════ Paleta LIGHT ═══════════════════════ */
const BG = "#EEF1F5";
const CARD = "#FFFFFF";
const INSET = "#E2E6EC";
const TEXT = "#0A1628";
const TEXT2 = "#4B5C6B";
const ACCENT = "#0A3747";
const ACCENT2 = "#E0E621";
const LINE_COLOR = "rgba(10,55,71,0.15)";

const neuCard = {
  background: CARD,
  borderRadius: 20,
  boxShadow: "6px 6px 14px rgba(163,177,198,0.35), -6px -6px 14px rgba(255,255,255,0.9)",
  border: "1px solid rgba(255,255,255,0.8)",
};
const neuInset = {
  background: INSET,
  borderRadius: 16,
  boxShadow: "inset 3px 3px 6px rgba(163,177,198,0.4), inset -3px -3px 6px rgba(255,255,255,0.7)",
};

const STATUS_MAP = {
  asignada:       { label: "Asignada",       color: "#2563EB", icon: User },
  agendada:       { label: "Agendada",       color: "#2563EB", icon: Clock },
  confirmada:     { label: "Confirmada",     color: "#7C3AED", icon: CheckCircle2 },
  en_ruta:        { label: "En Ruta",        color: "#D97706", icon: MapPin },
  en_ejecucion:   { label: "En Ejecución",   color: "#EA580C", icon: Wrench },
  en_sitio:       { label: "En Sitio",       color: "#0891B2", icon: MapPin },
  revision:       { label: "Revisión",       color: "#DC2626", icon: AlertTriangle },
  pendiente:      { label: "Pendiente",      color: "#64748B", icon: Clock },
  finalizada:     { label: "Finalizada",     color: "#059669", icon: CheckCircle2 },
  assigned:       { label: "Asignada",       color: "#2563EB", icon: User },
  in_progress:    { label: "En Ejecución",   color: "#EA580C", icon: Wrench },
  on_site:        { label: "En Sitio",       color: "#0891B2", icon: MapPin },
  manual_assignment_required: { label: "Revisión", color: "#DC2626", icon: AlertTriangle },
  pendiente_agendamiento: { label: "Pendiente", color: "#64748B", icon: Clock },
};

const getStatus = (s) => STATUS_MAP[s] || { label: s || "—", color: "#64748B", icon: Clock };

function fmtTime(iso) {
  if (!iso) return "Sin hora";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("es-EC", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "America/Guayaquil" });
  } catch { return "—"; }
}

/* ═══════════════════════ Nodo Timeline ═══════════════════════ */
function TimelineNode({ order, index, total, onSelect }) {
  const isLeft = index % 2 === 0;
  const st = getStatus(order.status_es || order.status);
  const StIcon = st.icon;
  const isLast = index === total - 1;

  const summary = order.task_name
    ? `${order.task_name}${order.solution_type_name ? ` · ${order.solution_type_name}` : ""}`
    : order.solution_type_name || "OT sin descripción";

  return (
    <div className="relative" style={{ minHeight: 130 }}>
      {/* Línea vertical central */}
      {!isLast && (
        <div
          className="absolute left-1/2 -translate-x-1/2"
          style={{ top: 60, bottom: -20, width: 2, background: LINE_COLOR }}
        />
      )}

      {/* Punto central */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: index * 0.05, type: "spring", stiffness: 400 }}
        className="absolute left-1/2 -translate-x-1/2 z-10"
        style={{ top: 28 }}
      >
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{
            background: CARD,
            border: `2.5px solid ${st.color}`,
            boxShadow: `3px 3px 8px rgba(163,177,198,0.4), -3px -3px 8px rgba(255,255,255,0.9), 0 0 0 3px ${st.color}15`,
          }}
        >
          <StIcon size={16} style={{ color: st.color }} />
        </div>
      </motion.div>

      {/* Conector horizontal */}
      <div
        className="absolute top-[47px] z-0"
        style={{
          left: isLeft ? "18%" : "50%",
          right: isLeft ? "50%" : "18%",
          height: 2,
          background: `linear-gradient(${isLeft ? "to left" : "to right"}, ${st.color}40, transparent)`,
        }}
      />

      {/* Tarjeta OT */}
      <motion.button
        initial={{ opacity: 0, x: isLeft ? -30 : 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.05 + 0.08, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => onSelect(order)}
        className="relative z-20 text-left w-[44%] group"
        style={{
          [isLeft ? "marginRight" : "marginLeft"]: "auto",
          [isLeft ? "marginLeft" : "marginRight"]: "0",
          display: "block",
          float: isLeft ? "left" : "right",
        }}
        data-testid={`timeline-node-${order.odoo_task_id || index}`}
      >
        <div
          className="p-4 transition-all duration-200"
          style={{
            ...neuCard,
            borderColor: `${st.color}15`,
          }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = `8px 8px 18px rgba(163,177,198,0.45), -6px -6px 14px rgba(255,255,255,0.95), 0 0 0 1px ${st.color}20`; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = neuCard.boxShadow; }}
        >
          {/* OT # + Badge */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-black tracking-wider" style={{ color: ACCENT }}>
              OT-{order.odoo_task_id || order.task_number || "?"}
            </span>
            <span
              className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
              style={{ background: `${st.color}12`, color: st.color }}
            >
              {st.label}
            </span>
          </div>

          {/* Cliente */}
          <p className="text-[13px] font-bold leading-tight mb-1 line-clamp-1" style={{ color: TEXT }}>
            {order.client_name || "Cliente no asignado"}
          </p>

          {/* Resumen */}
          <p className="text-[11px] leading-snug line-clamp-2 mb-2" style={{ color: TEXT2 }}>
            {summary}
          </p>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1 text-[10px]" style={{ color: TEXT2 }}>
                <Clock size={10} /> {fmtTime(order.scheduled_at)}
              </span>
              {order.priority && order.priority !== "baja" && (
                <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: order.priority === "urgente" ? "#DC2626" : "#D97706" }}>
                  <Zap size={10} /> {order.priority}
                </span>
              )}
            </div>
            <ChevronRight
              size={14}
              className="opacity-30 group-hover:opacity-100 transition-opacity"
              style={{ color: ACCENT }}
            />
          </div>
        </div>
      </motion.button>
      <div style={{ clear: "both" }} />
    </div>
  );
}

/* ═══════════════════════ Header ═══════════════════════ */
function TimelineHeader({ user, count, loading, onRefresh, onLogout, onBack }) {
  const now = new Date();
  const dateStr = now.toLocaleDateString("es-EC", {
    weekday: "long", day: "numeric", month: "long", timeZone: "America/Guayaquil",
  });

  return (
    <div
      className="sticky top-0 z-50 px-4 py-3"
      style={{ background: `${BG}ee`, backdropFilter: "blur(12px)" }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} className="p-2 rounded-xl" style={neuCard} data-testid="timeline-back-btn">
              <ArrowLeft size={18} style={{ color: ACCENT }} />
            </button>
          )}
          <div>
            <h1 className="text-lg font-black" style={{ color: TEXT }}>Mis Órdenes</h1>
            <p className="text-[11px] capitalize" style={{ color: TEXT2 }}>{dateStr}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onRefresh}
            disabled={loading}
            className="p-2 rounded-xl"
            style={neuCard}
            data-testid="timeline-refresh-btn"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} style={{ color: ACCENT }} />
          </button>
          {onLogout && (
            <button onClick={onLogout} className="p-2 rounded-xl" style={neuCard} data-testid="timeline-logout-btn">
              <LogOut size={16} style={{ color: "#DC2626" }} />
            </button>
          )}
        </div>
      </div>

      {/* KPI */}
      <div className="flex items-center gap-3">
        <div className="flex-1 rounded-2xl px-3 py-2.5 text-center" style={neuInset}>
          <span className="text-2xl font-black" style={{ color: ACCENT }}>{count}</span>
          <span className="text-[10px] block font-medium" style={{ color: TEXT2 }}>OTs Hoy</span>
        </div>
        <div className="flex-1 rounded-2xl px-3 py-2.5 text-center" style={neuInset}>
          <span className="text-base font-black" style={{ color: TEXT }}>
            {user?.name?.split(" ")[0] || "Técnico"}
          </span>
          <span className="text-[10px] block font-medium" style={{ color: TEXT2 }}>
            {user?.email?.split("@")[0] || "—"}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ Empty ═══════════════════════ */
function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-20 px-6 text-center"
    >
      <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4" style={neuInset}>
        <CheckCircle2 size={36} style={{ color: "#059669" }} />
      </div>
      <p className="text-base font-bold mb-1" style={{ color: TEXT }}>Sin órdenes pendientes</p>
      <p className="text-sm" style={{ color: TEXT2 }}>
        No tienes OTs asignadas para hoy. Descansa o consulta con tu supervisor.
      </p>
    </motion.div>
  );
}

/* ═══════════════════════ Página ═══════════════════════ */
export default function TechOrderTimeline() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [window_filter, setWindowFilter] = useState("today_tomorrow");

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const incCompleted = window_filter === "all" ? "&include_completed=true" : "";
      const { data } = await api.get(`/v1/mobile/my-orders?window=${window_filter}${incCompleted}`);
      setOrders(data.items || []);
    } catch (e) {
      console.error("Error loading orders:", e);
    } finally {
      setLoading(false);
    }
  }, [window_filter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleSelect = (order) => {
    const id = order.id || `MASTER-${order.odoo_task_id}`;
    navigate(`/tecnico/orden/${encodeURIComponent(id)}/centro-trabajo`);
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen" style={{ background: BG }} data-testid="tech-order-timeline">
      <TimelineHeader
        user={user}
        count={orders.length}
        loading={loading}
        onRefresh={fetchOrders}
        onLogout={handleLogout}
        onBack={() => navigate("/apps")}
      />

      {/* Filtros */}
      <div className="flex items-center justify-center gap-2 px-4 py-3">
        {[
          { key: "today", label: "Hoy" },
          { key: "today_tomorrow", label: "Hoy + Mañana" },
          { key: "week", label: "Semana" },
          { key: "all", label: "Todas" },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setWindowFilter(f.key)}
            className="px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all"
            style={window_filter === f.key
              ? { ...neuCard, color: ACCENT, border: `1.5px solid ${ACCENT}30` }
              : { ...neuInset, color: TEXT2 }
            }
            data-testid={`timeline-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && orders.length === 0 && (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin" style={{ color: ACCENT }} />
        </div>
      )}

      {!loading && orders.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="px-4 pb-24 pt-2 max-w-lg mx-auto">
          <AnimatePresence>
            {orders.map((order, idx) => (
              <TimelineNode
                key={order.id || order.odoo_task_id || idx}
                order={order}
                index={idx}
                total={orders.length}
                onSelect={handleSelect}
              />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
